import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';

interface ConflictData {
  lastModified: string;
  version?: number;
  modifiedBy?: string;
}

interface ConflictDetectionConfig {
  onConflictDetected?: (serverData: any, localData: any) => void;
  showToast?: boolean;
}

export function useConflictDetection<T extends Record<string, any>>(
  config: ConflictDetectionConfig = {}
) {
  const { onConflictDetected, showToast = true } = config;
  const [hasConflict, setHasConflict] = useState(false);
  const [conflictData, setConflictData] = useState<{
    server: T | null;
    local: T | null;
  }>({ server: null, local: null });
  
  const lastKnownData = useRef<ConflictData | null>(null);

  const updateLastKnown = useCallback((data: ConflictData) => {
    lastKnownData.current = data;
  }, []);

  const checkForConflicts = useCallback((
    serverData: T & ConflictData,
    localData: T & ConflictData
  ): boolean => {
    if (!lastKnownData.current) {
      updateLastKnown({
        lastModified: serverData.lastModified || serverData.updated_at,
        version: serverData.version,
        modifiedBy: serverData.modifiedBy || serverData.modified_by
      });
      return false;
    }

    const serverModified = new Date(serverData.lastModified || serverData.updated_at);
    const lastKnownModified = new Date(lastKnownData.current.lastModified);
    const localModified = new Date(localData.lastModified || localData.updated_at);

    // Check if server data has been modified since we last knew about it
    const serverHasNewer = serverModified > lastKnownModified;
    // Check if we have local changes that would conflict
    const hasLocalChanges = localModified > lastKnownModified;

    const conflictExists = serverHasNewer && hasLocalChanges;

    if (conflictExists) {
      setHasConflict(true);
      setConflictData({ server: serverData, local: localData });
      
      if (showToast) {
        toast.error("Data Conflict Detected", {
          description: "This data was updated elsewhere. Please resolve the conflict.",
          duration: Infinity
        });
      }
      
      onConflictDetected?.(serverData, localData);
    }

    return conflictExists;
  }, [onConflictDetected, showToast, updateLastKnown]);

  const resolveConflict = useCallback((resolution: 'server' | 'local' | 'merge', mergedData?: T) => {
    let resolvedData: T;

    switch (resolution) {
      case 'server':
        resolvedData = conflictData.server!;
        break;
      case 'local':
        resolvedData = conflictData.local!;
        break;
      case 'merge':
        if (!mergedData) {
          throw new Error('Merged data must be provided when using merge resolution');
        }
        resolvedData = mergedData;
        break;
      default:
        throw new Error('Invalid conflict resolution type');
    }

    // Update our known state
    updateLastKnown({
      lastModified: new Date().toISOString(),
      version: (resolvedData as any).version,
      modifiedBy: (resolvedData as any).modifiedBy
    });

    setHasConflict(false);
    setConflictData({ server: null, local: null });

    if (showToast) {
      toast.success("Conflict Resolved", {
        description: `Using ${resolution} version of the data.`
      });
    }

    return resolvedData;
  }, [conflictData, updateLastKnown, showToast]);

  const dismissConflict = useCallback(() => {
    setHasConflict(false);
    setConflictData({ server: null, local: null });
  }, []);

  return {
    hasConflict,
    conflictData,
    checkForConflicts,
    resolveConflict,
    dismissConflict,
    updateLastKnown
  };
}